import { useState, useCallback } from 'react';
import { handleInputChange, handleInputKeyDown } from '../utils/inputValidation';

/**
 * Custom hook for form validation with real-time input restrictions
 * @param {Object} initialFormData - Initial form data state
 * @param {Object} initialFormErrors - Initial form errors state
 * @returns {Object} - Form validation utilities and state
 */
export const useFormValidation = (initialFormData, initialFormErrors) => {
  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialFormErrors);

  /**
   * Handle input change with validation
   * @param {Event} e - The change event
   */
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    // For checkboxes, use 'checked', otherwise use 'value'
    let updatedValue = type === 'checkbox' ? checked : value;
    
    // Basic processing based on field type
    if (name === 'phone' && type !== 'checkbox') {
      // Limit to 10 digits maximum
      if (value.length > 10) {
        updatedValue = value.slice(0, 10);
      }
    }
    
    // Update form data using functional update to get current state
    setFormData(prevFormData => {
      const newFormData = {
        ...prevFormData,
        [name]: updatedValue
      };
      
      // Clear errors based on field type
      setFormErrors(prev => {
        const newErrors = { ...prev };
        
        // Clear error for the current field
        newErrors[name] = '';
        
        // Special handling for email/phone - if either has content, clear both "required" errors
        if (name === 'email' || name === 'phone') {
          const emailValue = name === 'email' ? updatedValue : newFormData.email;
          const phoneValue = name === 'phone' ? updatedValue : newFormData.phone;
          
          // If either email or phone has content, clear both "required" errors
          if (emailValue.trim() || phoneValue.trim()) {
            if (newErrors.email === 'Either email or phone number is required') {
              newErrors.email = '';
            }
            if (newErrors.phone === 'Either email or phone number is required') {
              newErrors.phone = '';
            }
          }
        }
        
        return newErrors;
      });
      
      return newFormData;
    });
  }, []);

  /**
   * Handle keydown to prevent invalid characters
   * @param {Event} e - The keydown event
   */
  const handleKeyDown = useCallback((e) => {
    const fieldName = e.target.name;
    handleInputKeyDown(e, fieldName);
  }, []);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setFormErrors(initialFormErrors);
  }, [initialFormData, initialFormErrors]);

  /**
   * Update form data directly
   * @param {Object} newData - New form data to set
   */
  const updateFormData = useCallback((newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  }, []);

  /**
   * Update form errors directly
   * @param {Object} newErrors - New form errors to set
   */
  const updateFormErrors = useCallback((newErrors) => {
    setFormErrors(prev => ({ ...prev, ...newErrors }));
  }, []);

  /**
   * Clear specific field error
   * @param {string} fieldName - Name of the field to clear error for
   */
  const clearFieldError = useCallback((fieldName) => {
    setFormErrors(prev => ({
      ...prev,
      [fieldName]: ''
    }));
  }, []);

  return {
    formData,
    formErrors,
    setFormData,
    setFormErrors,
    handleChange,
    handleKeyDown,
    resetForm,
    updateFormData,
    updateFormErrors,
    clearFieldError
  };
};
